import { lazy, Suspense, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  UNSAFE_LocationContext,
  useLocation,
  useOutlet,
  useParams,
} from 'react-router-dom';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { Header } from '@/widgets/Header';
import { BannerBar } from '@/features/banners';
import { markScrolled } from '@/features/banners/model/scrolled';
import { CommandPalette } from '@/features/command-palette';
import { MassIncidentHost } from '@/features/mass-incidents';
import { OnboardingTour } from '@/widgets/Tour';
import { WhatsNewModal } from '@/features/changelog';
import { Sidebar } from '@/widgets/Sidebar';
import { WORKFLOW_EDITOR_ENABLED } from '@/shared/config/admin-sections';
import { useUiStore } from '@/shared/lib/uiStore';
import { appScrollTop, scrollAppToTop, usePageScroll } from '@/shared/lib/pageScroll';
import { cn } from '@/shared/lib/cn';
import {
  Scroller,
  RouteEnteringContext,
  pageEnter,
  pageShown,
  pageEnterTransition,
  noWaapi,
} from '@/shared/ui';
import { useAuthStore, useAdminAccess, useSsoReturnPath, rememberReturnPath } from '@/features/auth';
import { RevealController, useAiAvailabilityStore } from '@/features/ai-assistant';
import { HomePage } from '@/pages/Home';
import { LoginPage } from '@/pages/Login';
import { TicketsListPage } from '@/pages/TicketsList';
import { MyTicketsPortalPage } from '@/pages/MyTicketsPortal';
import { TicketDetailPage } from '@/pages/TicketDetail';
import { CategoriesPage } from '@/pages/Categories';
import { SubcategoriesPage } from '@/pages/Subcategories';
import { SubcategoryDetailPage } from '@/pages/SubcategoryDetail';
import { AdminLayoutPage } from '@/pages/AdminLayout';
import { SettingsPage } from '@/pages/Settings';
import { NotificationsPage } from '@/pages/Notifications';
import { UserProfilePage } from '@/pages/UserProfile';
import { KpiLeaderboardPage } from '@/pages/KpiLeaderboard';

const SprintsPage = lazy(() => import('@/pages/Sprints'));
const MessengerPage = lazy(() => import('@/pages/Messenger'));
const AdminTaskStatusesPage = lazy(() => import('@/pages/AdminTaskStatuses'));
const AdminTaskLabelsPage = lazy(() => import('@/pages/AdminTaskLabels'));
const AnalyticsPage = lazy(() =>
  import('@/pages/Analytics').then((m) => ({ default: m.AnalyticsPage })),
);
const InstructionsPage = lazy(() =>
  import('@/pages/Instructions').then((m) => ({ default: m.InstructionsPage })),
);
const SystemStatusPage = lazy(() =>
  import('@/pages/SystemStatus').then((m) => ({ default: m.SystemStatusPage })),
);
import { NotFoundPage } from '@/pages/NotFound';
import { RouteErrorScreen } from './errors/ErrorScreen';
import styles from './AppLayout.module.css';

const AdminCategoriesPage = lazy(() =>
  import('@/pages/AdminCategories').then((m) => ({ default: m.AdminCategoriesPage })),
);
const AdminFormBuilderPage = lazy(() =>
  import('@/pages/AdminFormBuilder').then((m) => ({ default: m.AdminFormBuilderPage })),
);
const AdminFormTemplatesPage = lazy(() =>
  import('@/pages/AdminFormTemplates').then((m) => ({ default: m.AdminFormTemplatesPage })),
);
const AdminRolePermissionsPage = lazy(() =>
  import('@/pages/AdminRolePermissions').then((m) => ({ default: m.AdminRolePermissionsPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/AdminUsers').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminSupportGroupsPage = lazy(() =>
  import('@/pages/AdminSupportGroups').then((m) => ({ default: m.AdminSupportGroupsPage })),
);
const AdminChatsPage = lazy(() => import('@/pages/AdminChats').then((m) => ({ default: m.AdminChatsPage })));
const AdminKeyUsersPage = lazy(() =>
  import('@/pages/AdminKeyUsers').then((m) => ({ default: m.AdminKeyUsersPage })),
);
const CommitteePage = lazy(() =>
  import('@/pages/Committee').then((m) => ({ default: m.CommitteePage })),
);
const AdminApprovalCommitteesPage = lazy(() =>
  import('@/pages/AdminApprovalCommittees').then((m) => ({
    default: m.AdminApprovalCommitteesPage,
  })),
);
const AdminWorkflowsPage = lazy(() =>
  import('@/pages/AdminWorkflows').then((m) => ({ default: m.AdminWorkflowsPage })),
);
const WorkflowEditorPage = lazy(() =>
  import('@/pages/AdminWorkflows/WorkflowEditorPage').then((m) => ({ default: m.WorkflowEditorPage })),
);
const AdminMassIncidentsPage = lazy(() =>
  import('@/pages/AdminMassIncidents').then((m) => ({ default: m.AdminMassIncidentsPage })),
);
const AdminBannersPage = lazy(() =>
  import('@/pages/AdminBanners').then((m) => ({ default: m.AdminBannersPage })),
);
const AdminChangelogPage = lazy(() =>
  import('@/pages/AdminChangelog').then((m) => ({ default: m.AdminChangelogPage })),
);
const ChangelogPage = lazy(() =>
  import('@/pages/Changelog').then((m) => ({ default: m.ChangelogPage })),
);
const AdminAuditLogPage = lazy(() =>
  import('@/pages/AdminAuditLog').then((m) => ({ default: m.AdminAuditLogPage })),
);
const AdminErrorReportsPage = lazy(() =>
  import('@/pages/AdminErrorReports').then((m) => ({ default: m.AdminErrorReportsPage })),
);
const AdminServiceCatalogsPage = lazy(() =>
  import('@/pages/AdminServiceCatalogs').then((m) => ({ default: m.AdminServiceCatalogsPage })),
);
const AdminSlaPage = lazy(() => import('@/pages/AdminSla').then((m) => ({ default: m.AdminSlaPage })));
const AdminDirectoriesPage = lazy(() =>
  import('@/pages/AdminDirectories').then((m) => ({ default: m.AdminDirectoriesPage })),
);
const AdminFoldersPage = lazy(() =>
  import('@/pages/AdminFolders').then((m) => ({ default: m.AdminFoldersPage })),
);
const AdminClientsPage = lazy(() =>
  import('@/pages/AdminClients').then((m) => ({ default: m.AdminClientsPage })),
);
const AdminApiKeysPage = lazy(() =>
  import('@/pages/AdminApiKeys').then((m) => ({ default: m.AdminApiKeysPage })),
);
const AdminMailPage = lazy(() =>
  import('@/pages/AdminMail').then((m) => ({ default: m.AdminMailPage })),
);
const MailActionPage = lazy(() =>
  import('@/pages/MailAction').then((m) => ({ default: m.MailActionPage })),
);
const AdminCampaignsPage = lazy(() =>
  import('@/pages/AdminCampaigns').then((m) => ({ default: m.AdminCampaignsPage })),
);
const AdminWikiPage = lazy(() =>
  import('@/pages/AdminWiki').then((m) => ({ default: m.AdminWikiPage })),
);

const WikiArticlePage = lazy(() =>
  import('@/pages/WikiArticle').then((m) => ({ default: m.WikiArticlePage })),
);

function TicketsWorkspace() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  return hasPermission('view_tickets') ? <TicketsListPage /> : <Navigate to="/tickets" replace />;
}

function TicketDetailStaff() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { id } = useParams();
  return hasPermission('view_tickets') ? (
    <TicketDetailPage />
  ) : (
    <Navigate to={id ? `/tickets/${id}` : '/tickets'} replace />
  );
}

function portalOf(path: string): '/tickets' | '/approvals' | null {
  const m = /^\/(tickets|approvals)(?:\/([^/]+))?$/.exec(path);
  if (!m) return null;
  if (m[1] === 'tickets' && m[2] != null && ['workspace', 'new', 'view'].includes(m[2])) return null;
  return m[1] === 'tickets' ? '/tickets' : '/approvals';
}

function AnimatedOutlet({ onTransition }: { onTransition: (active: boolean) => void }) {
  const location = useLocation();
  const locationContext = useContext(UNSAFE_LocationContext);
  const outlet = useOutlet();

  const path = location.pathname;
  const isPortal = portalOf(path) === '/tickets';
  const isApprovals = path === '/approvals' || path.startsWith('/approvals/');
  const isMessenger = path === '/messenger' || path.startsWith('/messenger/');
  const animateKey =
    path === '/admin' || path.startsWith('/admin/')
      ? '/admin'
      : isPortal
        ? '/tickets'
        : isApprovals
          ? '/approvals'
          : isMessenger
            ? '/messenger'
            : path;

  const enteringRef = useRef(true);
  useLayoutEffect(() => {
    enteringRef.current = true;
    onTransition(true);
  }, [animateKey, onTransition]);

  return (
    <MotionConfig reducedMotion="user">
      <RouteEnteringContext.Provider value={enteringRef}>
        <AnimatePresence mode="wait">
          {outlet && (
            <motion.div
              key={animateKey}
              initial={pageEnter}
              animate={pageShown}
              exit={{ opacity: 0, transition: { duration: 0.09, ease: 'easeIn' } }}
              transition={pageEnterTransition}
              onUpdate={noWaapi}
              onAnimationComplete={(def) => {
                if ((def as { opacity?: number }).opacity !== 1) return;
                enteringRef.current = false;
                onTransition(false);
              }}
              className={styles.motion}
            >
              { }
              <UNSAFE_LocationContext.Provider value={locationContext}>{outlet}</UNSAFE_LocationContext.Provider>
            </motion.div>
          )}
        </AnimatePresence>
      </RouteEnteringContext.Provider>
    </MotionConfig>
  );
}

function AppLayout() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();
  useSsoReturnPath();
  const contentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const onRouteTransition = useCallback((active: boolean) => {
    mainRef.current?.toggleAttribute('data-route-anim', active);
  }, []);
  const pageScroll = usePageScroll();
  const prevPathRef = useRef(pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;
    if (prev !== pathname && portalOf(pathname) && portalOf(pathname) === portalOf(prev)) return;
    scrollAppToTop(pageScroll, contentRef.current);
    markScrolled(0);
  }, [pathname, pageScroll]);
  const onContentScroll = useCallback(() => {
    markScrolled(appScrollTop(false, contentRef.current));
  }, []);
  useEffect(() => {
    if (!pageScroll) return;
    const onScroll = () => markScrolled(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pageScroll]);
  const aiAvailable = useAiAvailabilityStore((s) => s.available);
  const aiEnabled = isAuthenticated && aiAvailable;
  const showSidebar = isAuthenticated;
  return (
    <RevealController enabled={aiEnabled}>
      { }
      <div className={cn(styles.layout, aiEnabled && styles.layoutGlass, !isAuthenticated && styles.layoutGuest)}>
        { }
        <BannerBar />
        <Header />
        { }
        <CommandPalette />
        {isAuthenticated && <MassIncidentHost />}
        <OnboardingTour />
        { }
        <WhatsNewModal />
        <div
          className={cn(
            styles.sidebarWrapper,
            !showSidebar ? styles.sidebarHidden : collapsed && styles.sidebarCollapsed,
          )}
        >
          {showSidebar && <Sidebar />}
          <Scroller
            ref={mainRef}
            element="main"
            className={styles.main}
            viewportClassName={styles.mainViewport}
            viewportRef={contentRef}
            onScroll={onContentScroll}
            page={pageScroll}
          >
            <AnimatedOutlet onTransition={onRouteTransition} />
          </Scroller>
        </div>
      </div>
    </RevealController>
  );
}

function adminEl(path: string, page: React.ReactElement) {
  return <AdminRoute path={path}>{page}</AdminRoute>;
}

function AdminRoute({ path, children }: { path: string; children: React.ReactNode }) {
  const { canAccessPath } = useAdminAccess();
  if (!canAccessPath(path)) return <AdminIndexRedirect />;
  return <Suspense fallback={null}>{children}</Suspense>;
}

function AdminIndexRedirect() {
  const { firstPath } = useAdminAccess();
  return <Navigate to={firstPath ?? '/'} replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  if (!isAuthenticated) {
    rememberReturnPath(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'login', element: <LoginPage /> },
      {
        path: 'mail/a/:token',
        element: (
          <Suspense fallback={null}>
            <MailActionPage />
          </Suspense>
        ),
      },
      {
        path: 'categories',
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <CategoriesPage /> },
          { path: ':categoryId/subcategories', element: <SubcategoriesPage /> },
          { path: ':categoryId/subcategories/:subcategoryId', element: <SubcategoryDetailPage /> },
        ],
      },
      {
        path: 'tickets',
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <MyTicketsPortalPage /> },
          { path: 'workspace', element: <TicketsWorkspace /> },
          { path: 'new', element: <Navigate to="/categories" replace /> },
          { path: 'view/:id', element: <TicketDetailStaff /> },
          { path: ':id', element: <MyTicketsPortalPage /> },
        ],
      },
      {
        path: 'changelog',
        element: (
          <ProtectedRoute>
            <ChangelogPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'approvals',
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <MyTicketsPortalPage mode="approvals" /> },
          { path: ':id', element: <MyTicketsPortalPage mode="approvals" /> },
        ],
      },
      {
        path: 'admin',
        element: (
          <ProtectedRoute>
            <AdminLayoutPage />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <AdminIndexRedirect /> },
          { path: 'categories', element: adminEl('/admin/categories', <AdminCategoriesPage />) },
          {
            path: 'categories/:subcategoryId/form',
            element: adminEl('/admin/categories/form', <AdminFormBuilderPage />),
          },
          { path: 'form-templates', element: adminEl('/admin/form-templates', <AdminFormTemplatesPage />) },
          { path: 'role-permissions', element: adminEl('/admin/role-permissions', <AdminRolePermissionsPage />) },
          { path: 'user-roles', element: <Navigate to="/admin/users" replace /> },
          { path: 'users', element: adminEl('/admin/users', <AdminUsersPage />) },
          { path: 'support-groups', element: adminEl('/admin/support-groups', <AdminSupportGroupsPage />) },
          { path: 'chats', element: adminEl('/admin/chats', <AdminChatsPage />) },
          { path: 'clients', element: adminEl('/admin/clients', <AdminClientsPage />) },
          { path: 'api-keys', element: adminEl('/admin/api-keys', <AdminApiKeysPage />) },
          {
            path: 'service-catalogs',
            element: adminEl('/admin/service-catalogs', <AdminServiceCatalogsPage />),
          },
          { path: 'sla', element: adminEl('/admin/sla', <AdminSlaPage />) },
          {
            path: 'directories',
            element: adminEl('/admin/directories', <AdminDirectoriesPage />),
          },
          { path: 'folders', element: adminEl('/admin/folders', <AdminFoldersPage />) },
          {
            path: 'task-statuses',
            element: adminEl('/admin/task-statuses', <AdminTaskStatusesPage />),
          },
          {
            path: 'task-labels',
            element: adminEl('/admin/task-labels', <AdminTaskLabelsPage />),
          },
          { path: 'key-users', element: adminEl('/admin/key-users', <AdminKeyUsersPage />) },
          {
            path: 'approval-committees',
            element: adminEl('/admin/approval-committees', <AdminApprovalCommitteesPage />),
          },
          {
            path: 'mass-incidents',
            element: adminEl('/admin/mass-incidents', <AdminMassIncidentsPage />),
          },
          ...(WORKFLOW_EDITOR_ENABLED
            ? [
                { path: 'workflows', element: adminEl('/admin/workflows', <AdminWorkflowsPage />) },
                { path: 'workflows/:id', element: adminEl('/admin/workflows', <WorkflowEditorPage />) },
              ]
            : []),
          { path: 'audit-log', element: adminEl('/admin/audit-log', <AdminAuditLogPage />) },
          { path: 'error-reports', element: adminEl('/admin/error-reports', <AdminErrorReportsPage />) },
          { path: 'banners', element: adminEl('/admin/banners', <AdminBannersPage />) },
          { path: 'changelog', element: adminEl('/admin/changelog', <AdminChangelogPage />) },
          { path: 'campaigns', element: adminEl('/admin/campaigns', <AdminCampaignsPage />) },
          { path: 'mail', element: adminEl('/admin/mail', <AdminMailPage />) },
          { path: 'wiki', element: adminEl('/admin/wiki', <AdminWikiPage />) },
        ],
      },
      { path: 'users/:id', element: <ProtectedRoute><UserProfilePage /></ProtectedRoute> },
      { path: 'kpi', element: <ProtectedRoute><KpiLeaderboardPage /></ProtectedRoute> },
      { path: 'analytics', element: <ProtectedRoute><AnalyticsPage /></ProtectedRoute> },
      { path: 'sprints', element: <ProtectedRoute><SprintsPage /></ProtectedRoute> },
      { path: 'committee', element: <ProtectedRoute><CommitteePage /></ProtectedRoute> },
      { path: 'messenger', element: <ProtectedRoute><MessengerPage /></ProtectedRoute> },
      { path: 'messenger/:chatId', element: <ProtectedRoute><MessengerPage /></ProtectedRoute> },
      { path: 'settings', element: <ProtectedRoute><SettingsPage /></ProtectedRoute> },
      { path: 'instructions', element: <ProtectedRoute><InstructionsPage /></ProtectedRoute> },
      {
        path: 'instructions/:urlId',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <WikiArticlePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      { path: 'notifications', element: <ProtectedRoute><NotificationsPage /></ProtectedRoute> },
      { path: 'status', element: <ProtectedRoute><SystemStatusPage /></ProtectedRoute> },
      ...(import.meta.env.DEV
        ? [
            {
              path: 'dev/ui',
              lazy: async () => ({ Component: (await import('@/pages/DevUi')).DevUiPage }),
            },
            {
              path: 'dev/ticket-layouts/:id?',
              lazy: async () => ({
                Component: (await import('@/pages/DevTicketLayouts')).DevTicketLayoutsPage,
              }),
            },
          ]
        : []),
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
